import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Text,
  DropdownMenu,
  Button,
  Checkbox,
  Flex,
} from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';

import Card from '../../../../shared/Card/Card.jsx';
import Placeholder from '../../../../shared/Placeholder/Placeholder.jsx';

import { useFetchPaginatedCampaigns } from '../../hooks/useFetchCampaigns';

import { default as SharedTable } from '../../../../shared/Table/Table.jsx';
import { campaignTableRow } from './TableRow.jsx';
import { columns, deletedColumns, filters, tabs } from './constants';
import useSort from '../../../../shared/Sort/useSort';
import { CampaignsTableContext } from './CampaignsTableContext.jsx';
import { STATUS_TYPES, DOT_COLORS } from './CalendarView/helpers.js';

// Note: "completed" status is treated as "active" (live)
const HIGHLIGHT_STATUS_ITEMS = [
  { id: STATUS_TYPES.ACTIVE, label: 'Live' },
  { id: STATUS_TYPES.DRAFT, label: 'Draft' },
  { id: STATUS_TYPES.ARCHIVED, label: 'Archived' },
];

export const Table = () => {
  const { t } = useTranslation('campaigns');
  const { userType } = useSelector(state => state.currentUser);
  const { collapsed } = useSelector(state => state.navBarCollapse);

  const [showAll, setShowAll] = useState(false);
  const [activeTab, setActiveTab] = useState(tabs.active.name);
  const [campaignStatus, setCampaignStatus] = useState(filters.ACTIVE);
  const [page, setPage] = useState(1);
  const [highlightedCampaignUuid, setHighlightedCampaignUuid] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const itemsPerPage = 10;

  const handleTabClick = useCallback(
    targetTab => {
      if (targetTab !== activeTab) {
        setPage(1);
      }
      setActiveTab(targetTab);
      if (targetTab === tabs.deleted.name) {
        setCampaignStatus(filters.DELETED);
      } else {
        setCampaignStatus(filters.ACTIVE);
      }
    },
    [activeTab],
  );

  const handleStatusFilterChange = statusId => {
    setSelectedStatusFilter(prev => {
      if (prev.includes(statusId)) {
        return prev.filter(id => id !== statusId);
      }
      return [...prev, statusId];
    });
    setPage(1);
  };

  const switchToTab = useCallback(
    tabName => {
      handleTabClick(tabName);
    },
    [handleTabClick],
  );

  const highlightCampaign = useCallback(uuid => {
    setHighlightedCampaignUuid(uuid);
    // Clear highlight after animation completes (3 seconds)
    setTimeout(() => {
      setHighlightedCampaignUuid(null);
    }, 3000);
  }, []);

  const setStatusFilters = useCallback(statusIds => {
    setSelectedStatusFilter(statusIds);
    setPage(1);
  }, []);

  const clearStatusFilters = useCallback(() => {
    setStatusFilters([]);
  }, [setStatusFilters]);

  const contextValue = useMemo(
    () => ({
      switchToTab,
      highlightCampaign,
      highlightedCampaignUuid,
      clearStatusFilters,
      setStatusFilters,
    }),
    [
      switchToTab,
      highlightCampaign,
      highlightedCampaignUuid,
      clearStatusFilters,
      setStatusFilters,
    ],
  );

  const onPageChange = data => setPage(data.selected + 1);

  const { sortingStatus, changeSort } = useSort({ persistInUrl: false });

  const {
    isLoading,
    data = { campaigns: [], pagy: {} },
  } = useFetchPaginatedCampaigns({
    page,
    itemsPerPage,
    showAll,
    // Use statusFilters for active tab (server-side filtering), campaignStatus for deleted tab
    // When no filters selected, use 'unarchived' to show all non-deleted campaigns
    campaignStatus:
      activeTab === tabs.deleted.name
        ? campaignStatus
        : selectedStatusFilter.length === 0
        ? filters.ACTIVE
        : undefined,
    statusFilters:
      activeTab === tabs.active.name && selectedStatusFilter.length > 0
        ? selectedStatusFilter
        : undefined,
    ...sortingStatus,
  });

  const { pagy } = data;
  const campaigns = data.campaigns;

  // Adjust minimum width based on navigation bar state
  const navBarWidth = collapsed ? 200 : 65;
  const adjustedMinWidth = `calc(1290px - ${navBarWidth}px)`;

  const filterDropdown =
    activeTab === tabs.active.name ? (
      <Box ml="auto">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <Button variant="outline" size="2">
              <i className="fa-regular fa-filter" />
              <Text size="2">{t('calendar.filter')}</Text>
              <i className="fa-solid fa-chevron-down" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            {HIGHLIGHT_STATUS_ITEMS.map(item => (
              <DropdownMenu.Item
                key={item.id}
                onSelect={e => {
                  e.preventDefault();
                  handleStatusFilterChange(item.id);
                }}
              >
                <Flex align="center" gap="2">
                  <Checkbox checked={selectedStatusFilter.includes(item.id)} />
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: DOT_COLORS[item.id],
                    }}
                  />
                  <Text size="2">{item.label}</Text>
                </Flex>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Box>
    ) : null;

  return (
    <CampaignsTableContext.Provider value={contextValue}>
      <Card
        title={t('tableOverview.tableTitle')}
        items={!showAll && campaigns.length ? itemsPerPage : undefined}
        pagy={!showAll && campaigns.length ? pagy : undefined}
        onPageChange={!showAll && campaigns.length ? onPageChange : undefined}
        onClick={() => setShowAll(!showAll)}
        canShowAll={campaigns.length > 0}
        isShowingAll={showAll}
        isLoading={isLoading}
        tabs={tabs}
        activeTab={activeTab}
        tabClick={handleTabClick}
        margin="0"
        headerContent={filterDropdown}
      >
        {activeTab === tabs.deleted.name && (
          <Box
            style={{
              borderBottom: '1px solid var(--gray-4)',
            }}
            p="4"
          >
            <Text size="2">{t('tableOverview.deletedTabInfo')}</Text>
          </Box>
        )}
        {campaigns.length ? (
          <SharedTable
            columns={activeTab === tabs.deleted.name ? deletedColumns : columns}
            body={campaignTableRow({
              campaigns: campaigns,
              userType,
              campaignStatus,
              highlightedCampaignUuid,
            })}
            sortingStatus={sortingStatus}
            changeSort={changeSort}
            minWidthBeforeScroll={adjustedMinWidth}
            id="engagement-table"
          />
        ) : (
          <Placeholder />
        )}
      </Card>
    </CampaignsTableContext.Provider>
  );
};
